"use client";

import type { ModuleProductStepper } from "@lib/types";
import { useEffect, useState } from "react";

import { ResponsiveImage } from "@/components/shared/ResponsiveImage";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselOptions,
} from "@/components/ui/carousel";
import { Container } from "@/components/ui/container";

type StepData = ModuleProductStepper["steps"][number];

type StepCardProps = {
  step: StepData;
  isHighlighted: boolean;
};

function StepCard({ step, isHighlighted }: StepCardProps) {
  return (
    <div
      className="rounded-lg p-px transition-colors duration-300"
      style={{
        background: isHighlighted
          ? "linear-gradient(180deg, var(--Color-Indigo-Lighter, rgba(189, 212, 252, 0.50)) 0%, var(--Color-Indigo-Dark, rgba(67, 108, 161, 0.50)) 100%)"
          : "transparent",
      }}
    >
      <div
        className="p-padding-element-lg rounded-lg transition-colors duration-300"
        style={{
          background: isHighlighted ? "rgba(55, 65, 81, 0.8)" : "transparent",
        }}
      >
        {step.icon && (
          <div className="mb-gap-element-lg flex justify-start">
            <div className="h-16 w-16">
              <ResponsiveImage
                src={step.icon}
                width={64}
                height={64}
                className="h-full w-full object-contain"
                imageOptions={{ desktopWidth: 64 }}
                useNextImage
              />
            </div>
          </div>
        )}

        <div className="space-y-gap-element-md text-left">
          <h3
            className={`typography-heading-4 ${isHighlighted ? "text-indigo-lighter" : "text-indigo-light"}`}
          >
            {step.title}
          </h3>
          <p
            className={`typography-text-medium-normal ${isHighlighted ? "text-indigo-lighter" : "text-indigo-light"}`}
          >
            {step.description}
          </p>
        </div>
      </div>
    </div>
  );
}

type StepperProps = {
  module: ModuleProductStepper;
};

const carouselOpts: CarouselOptions = {
  align: "start",
  slidesToScroll: 1,
  loop: false,
};

export function Stepper({ module }: StepperProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [timerKey, setTimerKey] = useState(0);

  useEffect(() => {
    if (!isHovered && module?.steps?.length) {
      const interval = setInterval(() => {
        setActiveStep((current) => (current + 1) % module.steps.length);
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [isHovered, module?.steps?.length, timerKey]);

  useEffect(() => {
    if (carouselApi) {
      carouselApi.scrollTo(activeStep);
    }
  }, [carouselApi, activeStep]);

  useEffect(() => {
    if (!carouselApi) return;

    const onSelect = () => {
      setActiveStep(carouselApi.selectedScrollSnap());
    };

    carouselApi.on("select", onSelect);
    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi]);

  if (!module?.steps?.length) return null;

  return (
    <section
      className="py-padding-section-lg"
      style={{
        background:
          "var(--gradient-blue-gradient, linear-gradient(180deg, var(--Color-Neutral-Darkest, #000003) 10%, var(--Color-Indigo-Darkest, #13233D) 52.89%, #000212 95.78%))",
      }}
    >
      <Container>
        <div className="mb-gap-element-xl max-w-5xl">
          <h3 className="typography-heading-3 mb-gap-element-md text-white">{module.title}</h3>
          {module.body && <p className="typography-text-large-normal text-white">{module.body}</p>}
        </div>

        <div className="relative mx-auto hidden max-w-5xl md:block lg:hidden">
          <div className="relative">
            <div className="space-y-gap-element-3xl">
              {module.steps.map((step, index) => {
                const isHighlighted = index === activeStep;

                return (
                  <div
                    key={step._key}
                    className="relative flex cursor-pointer transition-all duration-300"
                    onMouseEnter={() => {
                      setIsHovered(true);
                      setActiveStep(index);
                      setTimerKey((prev) => prev + 1);
                    }}
                    onMouseLeave={() => setIsHovered(false)}
                  >
                    <div className="mr-gap-element-xl pt-padding-element-sm relative z-10 shrink-0">
                      <div
                        className={`flex h-12 w-12 items-center justify-center rounded-full border-2 text-base font-bold transition-all duration-300 ${
                          isHighlighted
                            ? "text-neutral-darkest border-green-500 bg-green-500"
                            : "border-white bg-transparent text-white"
                        }`}
                      >
                        {index + 1}
                      </div>

                      {index < module.steps.length - 1 && (
                        <div
                          className="absolute top-18 left-1/2 w-px -translate-x-1/2 bg-white/30"
                          style={{ height: "50%" }}
                        ></div>
                      )}
                    </div>

                    <div className="flex-1">
                      <div
                        className="rounded-lg p-px transition-colors duration-300"
                        style={{
                          background: isHighlighted
                            ? "linear-gradient(180deg, var(--Color-Indigo-Lighter, rgba(189, 212, 252, 0.50)) 0%, var(--Color-Indigo-Dark, rgba(67, 108, 161, 0.50)) 100%)"
                            : "transparent",
                        }}
                      >
                        <div
                          className={`p-padding-element-lg rounded-lg transition-colors duration-300 ${isHighlighted ? "pl-padding-element-xl" : ""}`}
                          style={{
                            background: isHighlighted ? "rgba(55, 65, 81, 0.8)" : "transparent",
                          }}
                        >
                          <div
                            className={`flex items-start ${isHighlighted ? "gap-gap-element-xl" : "gap-gap-element-md pr-padding-section-sm"}`}
                          >
                            {step.icon && (
                              <div className="shrink-0">
                                <div className="h-16 w-16">
                                  <ResponsiveImage
                                    src={step.icon}
                                    width={64}
                                    height={64}
                                    className="h-full w-full object-contain"
                                    imageOptions={{ desktopWidth: 64 }}
                                    useNextImage
                                  />
                                </div>
                              </div>
                            )}

                            <div className="flex-1">
                              <div className="mb-gap-element-sm">
                                <h3
                                  className={`typography-heading-4 ${isHighlighted ? "text-indigo-lighter" : "text-white"}`}
                                >
                                  {step.title}
                                </h3>
                              </div>
                              <p
                                className={`typography-text-medium-normal ${isHighlighted ? "text-indigo-lighter" : "text-neutral-lighter"}`}
                              >
                                {step.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="relative mx-auto hidden max-w-6xl lg:block">
          <div className="relative">
            <div className="space-y-gap-element-3xl">
              {module.steps.map((step, index) => {
                const isOdd = (index + 1) % 2 === 1;
                const isHighlighted = index === activeStep;

                return (
                  <div
                    key={step._key}
                    className="relative flex cursor-pointer items-center transition-all duration-300"
                    onMouseEnter={() => {
                      setIsHovered(true);
                      setActiveStep(index);
                      setTimerKey((prev) => prev + 1);
                    }}
                    onMouseLeave={() => setIsHovered(false)}
                  >
                    {isOdd && (
                      <div className="pr-gap-element-2xl w-5/12">
                        <StepCard step={step} isHighlighted={isHighlighted} />
                      </div>
                    )}

                    <div className="absolute left-1/2 z-10 -translate-x-1/2 transform">
                      <div
                        className={`flex h-12 w-12 items-center justify-center rounded-full border-2 text-base font-bold transition-all duration-300 ${
                          isHighlighted
                            ? "text-neutral-darkest border-green-500 bg-green-500"
                            : "border-white bg-transparent text-white hover:scale-105"
                        }`}
                      >
                        {index + 1}
                      </div>

                      {index < module.steps.length - 1 && (
                        <div className="absolute top-28 left-1/2 h-[161px] w-px -translate-x-1/2 bg-white/30"></div>
                      )}
                    </div>

                    {isOdd && <div className="w-5/12"></div>}

                    {!isOdd && (
                      <div className="pl-gap-element-xl ml-auto w-5/12">
                        <StepCard step={step} isHighlighted={isHighlighted} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="md:hidden">
          <div className="mb-gap-element-xl">
            <div className="flex items-center">
              {module.steps.map((step, index) => {
                const isActive = index === activeStep;

                return (
                  <div
                    key={step._key}
                    className={`flex items-center ${index < module.steps.length - 1 ? "flex-1" : ""}`}
                  >
                    <button
                      className={`flex h-12 w-12 items-center justify-center rounded-full border text-base transition-all duration-300 ${
                        isActive
                          ? "text-neutral-darkest border-green-500 bg-green-500"
                          : "border-white bg-transparent text-white"
                      }`}
                      onClick={() => {
                        setActiveStep(index);
                        setTimerKey((prev) => prev + 1);
                      }}
                    >
                      {index + 1}
                    </button>

                    {index < module.steps.length - 1 && (
                      <div className="mx-gap-element-md h-px flex-1 bg-white/30"></div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <Carousel opts={carouselOpts} setApi={setCarouselApi} className="w-full">
            <CarouselContent className="-ml-gap-element-md">
              {module.steps.map((step, index) => (
                <CarouselItem key={step._key} className="pl-gap-element-md">
                  <StepCard step={step} isHighlighted={index === activeStep} />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>
      </Container>
    </section>
  );
}
